import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import {
  AI_MODEL_OPTIONS,
  DEFAULT_AI_MODEL_ID,
  resolveAiModelOption,
  type AiModelId,
} from '@oneup/shared';
import {
  aliceAvatarUrl as aliceAvatar,
  AliceHeader,
} from '@command-center/oneup-portal-ui';
import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  cn,
} from '@command-center/ui-system';
import {
  Building2,
  Camera,
  Check,
  ChevronDown,
  FileText,
  FolderKanban,
  Loader2,
  Mic,
  Paperclip,
  Send,
  ShieldCheck,
  Sparkles,
  UserRound,
  Wrench,
  X,
} from 'lucide-react';
import {
  askAlice,
  globalSearch,
  updateRecord,
  type AliceAttachmentPayload,
  type AliceMentionPayload,
  type AliceProposal,
  type SearchResult,
} from './api';
import { AliceCameraDialog } from './components/AliceCameraDialog';
import {
  display,
  useToast,
} from './lib';

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  attachmentNames?: string[];
  mentionLabels?: string[];
  toolCalls?: Array<{ tool: string; args: Record<string, unknown> }>;
  proposals?: Array<AliceProposal & { state?: 'pending' | 'applied' | 'dismissed' }>;
}

interface AliceAttachment extends AliceAttachmentPayload {
  id: string;
  kind: 'image' | 'pdf';
}

interface AliceMention extends AliceMentionPayload {
  subtitle: string;
  token: string;
}

interface ActiveMention {
  start: number;
  query: string;
}

export interface AliceLaunchers {
  openNewProject(mode?: 'manual' | 'siteplan'): void;
  openUpload(projectId?: string): void;
  openBrief(projectId: string): void;
  openMissingInfo(projectId: string): void;
}

interface SlashCommand {
  command: string;
  description: string;
}
const slashCommands: SlashCommand[] = [
  { command: '/new-project', description: 'Start the shared New Project flow' },
  { command: '/upload-document', description: 'Upload a document to the current project' },
  { command: '/search-projects', description: 'Search projects' },
  { command: '/search-clients', description: 'Search clients' },
  { command: '/search-documents', description: 'Search project documents' },
  { command: '/project-brief', description: 'Generate a brief for the current project' },
  { command: '/missing-info', description: 'Check missing information for the current project' },
  { command: '/summarize-project', description: 'Summarize the current project' },
  { command: '/summarize-document', description: 'Summarize the current document' },
];

const toolLabels: Record<string, string> = {
  search_records: 'Searched records',
  get_project: 'Read project',
  get_client: 'Read client',
  get_project_documents: 'Listed documents',
  get_document: 'Read document',
  get_site_plan_analysis: 'Read site plan analysis',
  get_pending_suggestions: 'Checked pending suggestions',
  get_project_activity: 'Read activity',
  get_field_provenance: 'Checked field source',
  find_missing_information: 'Checked missing information',
  generate_project_brief: 'Generated brief',
  propose_project_update: 'Proposed an update',
};

const MODEL_STORAGE_KEY = 'ecorp.alice.model';
const MAX_ATTACHMENTS = 4;
const MAX_ATTACHMENT_BYTES = 6 * 1024 * 1024;
const MAX_TOTAL_ATTACHMENT_BYTES = 12 * 1024 * 1024;
const MAX_MENTIONS = 5;

export function AlicePanel({
  onCollapse,
  context,
  launchers,
  initialPrompt,
  onPromptConsumed,
}: {
  onCollapse(): void;
  context: Record<string, unknown>;
  launchers: AliceLaunchers;
  initialPrompt?: string;
  onPromptConsumed?(): void;
}) {
  const { toast } = useToast();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      text: 'Hi, I’m Alice. I can summarize projects, trace facts to their sources, find missing information, and draft briefs. Type @ to add a Project or Client as context.',
    },
  ]);
  const [busy, setBusy] = useState(false);
  const [contextExpanded, setContextExpanded] = useState(false);
  const [useCurrentContext, setUseCurrentContext] = useState(true);
  const [attachments, setAttachments] = useState<AliceAttachment[]>([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [mentions, setMentions] = useState<AliceMention[]>([]);
  const [cursorIndex, setCursorIndex] = useState(0);
  const [mentionResults, setMentionResults] = useState<SearchResult[]>([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [highlightedMention, setHighlightedMention] = useState(0);
  const [modelId, setModelId] = useState<AiModelId>(() => readModelPreference());
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const dictationBaseRef = useRef('');
  const selectedModel = resolveAiModelOption(modelId);

  const dictation = useSpeechDictation((transcript) => {
    const base = dictationBaseRef.current;
    setMessage(`${base}${base && transcript ? ' ' : ''}${transcript}`);
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);
  useEffect(() => {
    if (initialPrompt) {
      setMessage(initialPrompt);
      onPromptConsumed?.();
      window.setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [initialPrompt, onPromptConsumed]);

  const projectId = typeof context.projectId === 'string' ? context.projectId : undefined;
  const documentId = typeof context.documentId === 'string' ? context.documentId : undefined;
  const activeMention = useMemo(
    () => getActiveMention(message, cursorIndex),
    [cursorIndex, message],
  );
  const mentionQuery = activeMention?.query.trim().slice(0, 80) ?? '';

  useEffect(() => {
    if (!activeMention || !mentionQuery) {
      setMentionResults([]);
      setMentionLoading(false);
      return;
    }
    let cancelled = false;
    setMentionLoading(true);
    const timer = window.setTimeout(() => {
      void globalSearch(mentionQuery)
        .then((payload) => {
          if (!cancelled)
            setMentionResults(
              payload.results
                .filter(
                  (item) =>
                    item.resourceType === 'project' ||
                    item.resourceType === 'client' ||
                    item.resourceType === 'contact',
                )
                .slice(0, 6),
            );
        })
        .catch(() => {
          if (!cancelled) setMentionResults([]);
        })
        .finally(() => {
          if (!cancelled) setMentionLoading(false);
        });
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [activeMention?.start, mentionQuery]);

  useEffect(() => setHighlightedMention(0), [mentionQuery, mentionResults.length]);

  const slashMatches = useMemo(
    () =>
      message.startsWith('/')
        ? slashCommands.filter((item) =>
            item.command.startsWith(message.trim().split(/\s/)[0] ?? ''),
          )
        : [],
    [message],
  );

  function runSlash(command: string): boolean {
    const clean = command.trim();
    if (clean === '/new-project') {
      launchers.openNewProject();
      return true;
    }
    if (clean === '/upload-document') {
      launchers.openUpload(projectId);
      return true;
    }
    if (clean === '/project-brief') {
      if (projectId) launchers.openBrief(projectId);
      else toast('Open or mention a project first.', 'info');
      return true;
    }
    if (clean === '/missing-info') {
      if (projectId) launchers.openMissingInfo(projectId);
      else toast('Open or mention a project first.', 'info');
      return true;
    }
    if (clean.startsWith('/search-projects')) {
      void send(
        `Search projects: ${clean.replace('/search-projects', '').trim() || 'recently updated'}`,
      );
      return true;
    }
    if (clean.startsWith('/search-clients')) {
      void send(`Search clients: ${clean.replace('/search-clients', '').trim() || 'all'}`);
      return true;
    }
    if (clean.startsWith('/search-documents')) {
      void send(
        `Search project documents: ${clean.replace('/search-documents', '').trim() || 'recent'}`,
      );
      return true;
    }
    if (clean === '/summarize-project') {
      if (projectId) void send('Summarize the current project.');
      else toast('Open or mention a project first.', 'info');
      return true;
    }
    if (clean === '/summarize-document') {
      if (documentId) void send('Summarize the current document.');
      else toast('Open a document first.', 'info');
      return true;
    }
    return false;
  }

  async function send(text: string) {
    const clean = text.trim();
    if ((!clean && !attachments.length) || busy) return;
    const prompt = clean || 'Please review the attached file.';
    const turnAttachments = attachments;
    const turnMentions = mentions;
    setMessage('');
    setAttachments([]);
    dictation.stop();
    const nextMessages: ChatMessage[] = [
      ...messages,
      {
        role: 'user',
        text: prompt,
        attachmentNames: turnAttachments.map((item) => item.name),
        mentionLabels: turnMentions.map(
          (item) => `${item.type === 'project' ? 'Project' : 'Client'}: ${item.name}`,
        ),
      },
    ];
    setMessages(nextMessages);
    setBusy(true);
    try {
      const history = nextMessages
        .filter((item) => item.text)
        .slice(-12)
        .map((item) => ({ role: item.role, content: item.text }));
      const result = await askAlice(
        history,
        useCurrentContext ? safePageContext(context) : { pageContextAttached: false },
        {
          model: selectedModel.model,
          attachments: turnAttachments.map(({ name, mimeType, dataUrl }) => ({
            name,
            mimeType,
            dataUrl,
          })),
          mentions: turnMentions.map(({ type, id, name }) => ({ type, id, name })),
        },
      );
      setMessages((current) => [
        ...current,
        {
          role: 'assistant',
          text: result.answer,
          toolCalls: result.toolCalls,
          proposals: result.proposals.map((proposal) => ({
            ...proposal,
            state: 'pending' as const,
          })),
        },
      ]);
    } catch (cause) {
      setMessages((current) => [
        ...current,
        {
          role: 'assistant',
          text: cause instanceof Error ? cause.message : 'Alice is unavailable right now.',
        },
      ]);
    } finally {
      setBusy(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const text = message.trim();
    if (text.startsWith('/') && runSlash(text)) {
      setMessage('');
      return;
    }
    void send(text);
  }

  function onComposerKeyDown(event: ReactKeyboardEvent<HTMLTextAreaElement>) {
    if (activeMention && mentionResults.length) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedMention((value) => (value + 1) % mentionResults.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedMention(
          (value) => (value - 1 + mentionResults.length) % mentionResults.length,
        );
        return;
      }
      if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        selectMention(mentionResults[highlightedMention] ?? mentionResults[0]!);
        return;
      }
    }
    if (event.key === 'Escape' && dictation.listening) {
      event.preventDefault();
      dictation.stop();
      return;
    }
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      formRef.current?.requestSubmit();
    }
  }

  function selectMention(result: SearchResult) {
    if (
      !activeMention ||
      (result.resourceType !== 'project' &&
        result.resourceType !== 'client' &&
        result.resourceType !== 'contact')
    )
      return;
    const token = mentionToken(result.title);
    const before = message.slice(0, activeMention.start);
    const after = message.slice(cursorIndex);
    const next = `${before}${token} ${after}`;
    const nextCursor = before.length + token.length + 1;
    const mention: AliceMention = {
      type: result.resourceType,
      id: result.recordId,
      name: result.title,
      subtitle: result.subtitle,
      token,
    };
    setMessage(next);
    setMentions((current) =>
      current.some((item) => item.type === mention.type && item.id === mention.id)
        ? current
        : [...current, mention].slice(-MAX_MENTIONS),
    );
    setMentionResults([]);
    setCursorIndex(nextCursor);
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  }

  function removeMention(mention: AliceMention) {
    setMentions((current) =>
      current.filter((item) => item.type !== mention.type || item.id !== mention.id),
    );
    setMessage((current) =>
      current.replace(new RegExp(`${escapeRegExp(mention.token)}\\s?`), '').replace(/ {2,}/g, ' '),
    );
  }

  async function pickFiles(files: FileList | null) {
    if (!files?.length) return;
    const room = MAX_ATTACHMENTS - attachments.length;
    if (room <= 0) {
      toast(`Alice accepts up to ${MAX_ATTACHMENTS} attachments per message.`, 'info');
      return;
    }
    const accepted = Array.from(files).slice(0, room);
    const prepared: AliceAttachment[] = [];
    for (const file of accepted) {
      try {
        prepared.push(await prepareAttachment(file));
      } catch (cause) {
        toast(cause instanceof Error ? cause.message : `Could not attach ${file.name}.`, 'error');
      }
    }
    const existingBytes = attachments.reduce(
      (total, item) => total + dataUrlBytes(item.dataUrl),
      0,
    );
    let totalBytes = existingBytes;
    const withinBudget = prepared.filter((item) => {
      totalBytes += dataUrlBytes(item.dataUrl);
      return totalBytes <= MAX_TOTAL_ATTACHMENT_BYTES;
    });
    if (withinBudget.length < prepared.length)
      toast('Alice attachments must total 12 MB or less.', 'error');
    setAttachments((current) => [...current, ...withinBudget].slice(0, MAX_ATTACHMENTS));
  }

  function attachCameraPhoto(dataUrl: string) {
    if (attachments.length >= MAX_ATTACHMENTS) return;
    if (dataUrlBytes(dataUrl) > MAX_ATTACHMENT_BYTES) {
      toast('That photo is too large to attach.', 'error');
      return;
    }
    setAttachments((current) =>
      [
        ...current,
        {
          id: crypto.randomUUID(),
          name: `camera-photo-${Date.now()}.jpg`,
          mimeType: 'image/jpeg',
          dataUrl,
          kind: 'image' as const,
        },
      ].slice(0, MAX_ATTACHMENTS),
    );
  }

  function toggleDictation() {
    if (!dictation.supported) {
      toast('Voice dictation needs Chrome or Edge.', 'info');
      return;
    }
    if (dictation.listening) {
      dictation.stop();
      return;
    }
    dictationBaseRef.current = message.trimEnd();
    textareaRef.current?.focus();
    dictation.start();
  }

  function changeModel(next: AiModelId) {
    setModelId(next);
    localStorage.setItem(MODEL_STORAGE_KEY, next);
  }

  async function confirmProposal(
    messageIndex: number,
    proposalIndex: number,
    proposal: AliceProposal,
  ) {
    try {
      await updateRecord('projects', proposal.projectRecordId, {
        [proposal.fieldKey]: proposal.proposedValue,
      });
      setMessages((current) =>
        current.map((item, index) =>
          index === messageIndex
            ? {
                ...item,
                proposals: item.proposals?.map((entry, entryIndex) =>
                  entryIndex === proposalIndex ? { ...entry, state: 'applied' } : entry,
                ),
              }
            : item,
        ),
      );
      toast(`Updated ${proposal.fieldKey}.`);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Update failed.', 'error');
    }
  }

  const cameraAvailable =
    typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

  return (
    <div className="flex h-full min-h-0 flex-col bg-card text-card-foreground">
      <AliceHeader
        assistantName="Alice"
        avatarPath={aliceAvatar}
        subtitle="Context-aware help for this workspace."
        metaLabel={
          typeof context.projectName === 'string'
            ? `Project: ${context.projectName}`
            : typeof context.route === 'string'
              ? `Page: ${context.route}`
              : 'ECORP workspace'
        }
        badge={
          <Badge className="border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400">
            <span className="mr-1 h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Online
          </Badge>
        }
        onCollapse={onCollapse}
      />

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        <AliceContextEngine
          context={context}
          mentions={mentions}
          expanded={contextExpanded}
          enabled={useCurrentContext}
          onExpandedChange={setContextExpanded}
          onEnabledChange={setUseCurrentContext}
        />
        <div className="space-y-3">
          {messages.map((item, messageIndex) => (
            <div
              key={messageIndex}
              className={cn('space-y-2', item.role === 'user' ? 'ml-8' : 'mr-4')}
            >
              {item.toolCalls && item.toolCalls.length > 0 && (
                <details className="rounded-lg border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">
                  <summary className="flex cursor-pointer items-center gap-1.5">
                    <Wrench className="h-3 w-3" />
                    {item.toolCalls.length} lookup{item.toolCalls.length === 1 ? '' : 's'}
                    <ChevronDown className="ml-auto h-3 w-3" />
                  </summary>
                  <ul className="mt-1 space-y-0.5 pl-4">
                    {item.toolCalls.map((call, index) => (
                      <li key={index}>{toolLabels[call.tool] ?? call.tool}</li>
                    ))}
                  </ul>
                </details>
              )}
              <div
                className={cn(
                  'whitespace-pre-wrap rounded-xl p-3 text-sm',
                  item.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted',
                )}
              >
                {item.text}
              </div>
              {item.attachmentNames?.length || item.mentionLabels?.length ? (
                <div className="flex flex-wrap justify-end gap-1 text-[11px] text-muted-foreground">
                  {item.attachmentNames?.map((name) => (
                    <span key={name} className="rounded-full border px-2 py-0.5">
                      <Paperclip className="mr-1 inline h-3 w-3" />
                      {name}
                    </span>
                  ))}
                  {item.mentionLabels?.map((label) => (
                    <span key={label} className="rounded-full border px-2 py-0.5">
                      @ {label}
                    </span>
                  ))}
                </div>
              ) : null}
              {item.proposals?.map((proposal, proposalIndex) => (
                <div
                  key={proposalIndex}
                  className="rounded-xl border border-primary/40 bg-primary/5 p-3 text-sm"
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                    Proposed update — needs your confirmation
                  </p>
                  <p className="mt-1">
                    <span className="text-muted-foreground">Project:</span>{' '}
                    {proposal.projectName || proposal.projectRecordId}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Field:</span> {proposal.fieldKey}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Current:</span>{' '}
                    {display(proposal.existingValue) || 'Empty'}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Proposed:</span>{' '}
                    <span className="font-medium">{display(proposal.proposedValue)}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Source: {proposal.source} · {proposal.reason}
                  </p>
                  {proposal.state === 'applied' ? (
                    <p className="mt-2 inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                      <Check className="h-4 w-4" />
                      Applied
                    </p>
                  ) : proposal.state === 'dismissed' ? (
                    <p className="mt-2 text-muted-foreground">Dismissed</p>
                  ) : (
                    <div className="mt-2 flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => void confirmProposal(messageIndex, proposalIndex, proposal)}
                      >
                        Apply
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setMessages((current) =>
                            current.map((entry, index) =>
                              index === messageIndex
                                ? {
                                    ...entry,
                                    proposals: entry.proposals?.map((candidate, candidateIndex) =>
                                      candidateIndex === proposalIndex
                                        ? { ...candidate, state: 'dismissed' }
                                        : candidate,
                                    ),
                                  }
                                : entry,
                            ),
                          )
                        }
                      >
                        Dismiss
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
          {busy && (
            <div className="mr-4 flex items-center gap-2 rounded-xl bg-muted p-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Working…
            </div>
          )}
        </div>
      </div>

      <form ref={formRef} onSubmit={submit} className="relative border-t bg-background/70 p-3">
        {slashMatches.length > 0 && (
          <div className="absolute bottom-full left-3 right-3 z-30 mb-1 rounded-lg border bg-popover p-1 shadow-lg">
            {slashMatches.map((item) => (
              <button
                key={item.command}
                type="button"
                className="flex w-full items-baseline gap-2 rounded-md px-3 py-1.5 text-left text-sm hover:bg-muted"
                onClick={() => {
                  if (!runSlash(item.command)) setMessage(`${item.command} `);
                  else setMessage('');
                }}
              >
                <span className="font-mono text-primary">{item.command}</span>
                <span className="truncate text-xs text-muted-foreground">{item.description}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn('h-8 w-8', dictation.listening && 'text-destructive')}
            onClick={toggleDictation}
            title={
              dictation.supported
                ? dictation.listening
                  ? 'Stop voice dictation'
                  : 'Start voice dictation'
                : 'Voice needs Chrome or Edge'
            }
            aria-pressed={dictation.listening}
          >
            <Mic className={cn('h-4 w-4', dictation.listening && 'animate-pulse')} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => fileInputRef.current?.click()}
            disabled={attachments.length >= MAX_ATTACHMENTS}
            title="Attach an image or PDF"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setCameraOpen(true)}
            disabled={!cameraAvailable || attachments.length >= MAX_ATTACHMENTS}
            title={cameraAvailable ? 'Capture a photo' : 'Camera not available'}
          >
            <Camera className="h-4 w-4" />
          </Button>
          <AliceModelSwitcher modelId={modelId} onChange={changeModel} />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/gif,image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(event) => {
            void pickFiles(event.target.files);
            event.target.value = '';
          }}
        />

        {attachments.length > 0 && (
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
            {attachments.map((item) => (
              <div
                key={item.id}
                className="relative flex h-14 min-w-14 max-w-36 items-center gap-2 rounded-lg border bg-muted/30 p-1.5 pr-6"
              >
                {item.kind === 'image' ? (
                  <img
                    src={item.dataUrl}
                    alt=""
                    className="h-10 w-10 flex-shrink-0 rounded object-cover"
                  />
                ) : (
                  <FileText className="h-7 w-7 flex-shrink-0 text-primary" />
                )}
                <span className="truncate text-[10px] text-muted-foreground">{item.name}</span>
                <button
                  type="button"
                  className="absolute right-1 top-1 rounded-full bg-background p-0.5 text-muted-foreground hover:text-foreground"
                  onClick={() =>
                    setAttachments((current) =>
                      current.filter((candidate) => candidate.id !== item.id),
                    )
                  }
                  aria-label={`Remove ${item.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {mentions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {mentions.map((mention) => (
              <span
                key={`${mention.type}:${mention.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2 py-1 text-[11px]"
              >
                {mention.type === 'project' ? (
                  <FolderKanban className="h-3 w-3 text-primary" />
                ) : (
                  <Building2 className="h-3 w-3 text-primary" />
                )}
                <span className="max-w-48 truncate">{mention.name}</span>
                <button
                  type="button"
                  onClick={() => removeMention(mention)}
                  aria-label={`Remove ${mention.name} context`}
                >
                  <X className="h-3 w-3 text-muted-foreground" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="relative mt-2 rounded-lg border border-input bg-background focus-within:ring-1 focus-within:ring-ring">
          <textarea
            ref={textareaRef}
            className="min-h-[96px] max-h-44 w-full resize-y rounded-lg bg-transparent px-3 py-2.5 text-[13px] leading-relaxed outline-none placeholder:text-muted-foreground"
            value={message}
            onChange={(event) => {
              setMessage(event.target.value);
              setCursorIndex(event.currentTarget.selectionStart);
            }}
            onSelect={(event) => setCursorIndex(event.currentTarget.selectionStart)}
            onClick={(event) => setCursorIndex(event.currentTarget.selectionStart)}
            onKeyUp={(event) => setCursorIndex(event.currentTarget.selectionStart)}
            onKeyDown={onComposerKeyDown}
            placeholder="Ask Alice to draft, summarize, find, or update… Type @ for context."
            aria-label="Message Alice"
            disabled={busy}
          />
          {activeMention && mentionQuery && (
            <div className="absolute bottom-full left-0 right-0 z-30 mb-1 overflow-hidden rounded-lg border bg-popover shadow-lg">
              <div className="border-b px-3 py-2 text-[11px] font-medium text-muted-foreground">
                Add a Project, Client, or Contact
              </div>
              {mentionLoading ? (
                <div className="flex items-center gap-2 px-3 py-3 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Searching…
                </div>
              ) : mentionResults.length ? (
                mentionResults.map((result, index) => (
                  <button
                    key={`${result.resourceType}:${result.recordId}`}
                    type="button"
                    className={cn(
                      'flex w-full items-start gap-2 px-3 py-2 text-left',
                      index === highlightedMention ? 'bg-primary/10' : 'hover:bg-muted',
                    )}
                    onMouseEnter={() => setHighlightedMention(index)}
                    onClick={() => selectMention(result)}
                  >
                    {result.resourceType === 'project' ? (
                      <FolderKanban className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    ) : result.resourceType === 'contact' ? (
                      <UserRound className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    ) : (
                      <Building2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    )}
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-medium">{result.title}</span>
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {result.resourceType === 'project'
                          ? 'Project'
                          : result.resourceType === 'contact'
                            ? 'Contact'
                            : 'Client'}
                        {result.subtitle ? ` · ${result.subtitle}` : ''}
                      </span>
                    </span>
                  </button>
                ))
              ) : (
                <div className="px-3 py-3 text-xs text-muted-foreground">
                  No matching Projects, Clients, or Contacts.
                </div>
              )}
            </div>
          )}
          <div className="flex items-center justify-between gap-3 border-t px-2.5 py-2">
            <span className="text-[11px] text-muted-foreground">Cmd/Ctrl + Enter to send</span>
            <Button
              size="sm"
              className="h-8 gap-1.5 px-3"
              disabled={busy || (!message.trim() && !attachments.length)}
              aria-label="Send to Alice"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              Send
            </Button>
          </div>
        </div>
      </form>

      <AliceCameraDialog
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        onCapture={attachCameraPhoto}
      />
    </div>
  );
}

function AliceContextEngine({
  context,
  mentions,
  expanded,
  enabled,
  onExpandedChange,
  onEnabledChange,
}: {
  context: Record<string, unknown>;
  mentions: AliceMention[];
  expanded: boolean;
  enabled: boolean;
  onExpandedChange(value: boolean): void;
  onEnabledChange(value: boolean): void;
}) {
  const projectName = typeof context.projectName === 'string' ? context.projectName : undefined;
  const documentName = typeof context.documentName === 'string' ? context.documentName : undefined;
  const pending = typeof context.pendingSuggestions === 'number' ? context.pendingSuggestions : 0;
  const page = pageLabel(context);
  const attachedCount = (projectName ? 1 : 0) + (documentName ? 1 : 0) + mentions.length || 1;
  const summary = projectName
    ? `Context: ${page} — ${projectName}`
    : `Using current page context · ${attachedCount} item${attachedCount === 1 ? '' : 's'}`;
  return (
    <section className="mb-4 rounded-lg border bg-background/70">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[12px] text-muted-foreground"
        onClick={() => onExpandedChange(!expanded)}
        aria-expanded={expanded}
      >
        <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
        <span className="min-w-0 flex-1 truncate">{summary}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} />
      </button>
      {expanded && (
        <div className="border-t px-3 py-3">
          <label className="mb-3 flex items-center justify-between gap-3 text-[12px] text-muted-foreground">
            <span>Attach page context to next message</span>
            <Switch
              checked={enabled}
              onCheckedChange={onEnabledChange}
              aria-label="Use current page as context"
            />
          </label>
          <div className="space-y-1.5 text-[12px]">
            <ContextFact label="Current page" value={page} />
            <ContextFact label="Current project" value={projectName ?? 'None selected'} />
            <ContextFact label="Current document" value={documentName ?? 'None selected'} />
            <ContextFact
              label="Mentioned context"
              value={
                mentions.length
                  ? mentions
                      .map(
                        (item) => `${item.type === 'project' ? 'Project' : 'Client'}: ${item.name}`,
                      )
                      .join(', ')
                  : 'None'
              }
            />
            <ContextFact
              label="Warnings"
              value={
                pending ? `${pending} unreviewed suggestion${pending === 1 ? '' : 's'}` : 'None'
              }
            />
          </div>
        </div>
      )}
    </section>
  );
}

function ContextFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words text-foreground">{value}</span>
    </div>
  );
}

function AliceModelSwitcher({
  modelId,
  onChange,
}: {
  modelId: AiModelId;
  onChange(value: AiModelId): void;
}) {
  const selected = resolveAiModelOption(modelId);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="ml-auto inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
        >
          <Sparkles className="h-3 w-3 text-primary" />
          <span className="max-w-[150px] truncate">{selected.label}</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-1.5">
        <p className="px-2 py-1.5 text-[11px] font-medium text-muted-foreground">Alice model</p>
        {AI_MODEL_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onChange(option.id)}
            className={cn(
              'flex w-full flex-col gap-0.5 rounded-md px-2 py-1.5 text-left transition',
              option.id === modelId ? 'bg-primary/10' : 'hover:bg-muted/60',
            )}
          >
            <span className="flex items-center gap-1.5 text-[12px] font-medium">
              {option.id === modelId && <Sparkles className="h-3 w-3 text-primary" />}
              {option.label}
            </span>
            <span className="text-[11px] leading-4 text-muted-foreground">
              {option.description}
            </span>
          </button>
        ))}
        <p className="px-2 pb-1 pt-1.5 text-[10px] leading-3.5 text-muted-foreground">
          Saved on this device and validated by the Ecorp API before use.
        </p>
      </PopoverContent>
    </Popover>
  );
}

function safePageContext(context: Record<string, unknown>): Record<string, unknown> {
  const keys = [
    'route',
    'resource',
    'projectId',
    'projectName',
    'documentId',
    'documentName',
    'pendingSuggestions',
  ];
  return {
    pageContextAttached: true,
    ...Object.fromEntries(
      keys.flatMap((key) => {
        const value = context[key];
        return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
          ? [[key, value]]
          : [];
      }),
    ),
  };
}

function pageLabel(context: Record<string, unknown>): string {
  const route = typeof context.route === 'string' ? context.route : '';
  if (route === 'project' || route.startsWith('/projects/')) return 'Project Workspace';
  if (route.startsWith('/clients')) return 'Clients';
  if (route.startsWith('/documents')) return 'Documents';
  if (route.startsWith('/approvals')) return 'Approvals';
  return 'Ecorp Admin';
}

function readModelPreference(): AiModelId {
  if (typeof window === 'undefined') return DEFAULT_AI_MODEL_ID;
  const saved = window.localStorage.getItem(MODEL_STORAGE_KEY);
  return AI_MODEL_OPTIONS.some((option) => option.id === saved)
    ? (saved as AiModelId)
    : DEFAULT_AI_MODEL_ID;
}

function getActiveMention(value: string, cursor: number): ActiveMention | null {
  if (cursor < 0 || cursor > value.length) return null;
  const beforeCursor = value.slice(0, cursor);
  const atIndex = beforeCursor.lastIndexOf('@');
  if (atIndex < 0) return null;
  const previous = atIndex > 0 ? value[atIndex - 1] : '';
  if (previous && /[A-Za-z0-9_]/.test(previous)) return null;
  const query = beforeCursor.slice(atIndex + 1);
  if (
    !query ||
    query.length > 80 ||
    query.includes('\n') ||
    /^\s/.test(query) ||
    /\s$/.test(query) ||
    /[!?.,;:]$/.test(query)
  )
    return null;
  return { start: atIndex, query };
}

function mentionToken(value: string): string {
  const slug = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
  return `@${slug || 'context'}`;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

async function prepareAttachment(file: File): Promise<AliceAttachment> {
  const allowed = new Set([
    'application/pdf',
    'image/gif',
    'image/jpeg',
    'image/png',
    'image/webp',
  ]);
  if (!allowed.has(file.type))
    throw new Error(`${file.name} must be a PDF, PNG, JPG, GIF, or WEBP file.`);
  let dataUrl: string;
  let mimeType = file.type;
  if (file.type === 'application/pdf' || file.size <= MAX_ATTACHMENT_BYTES) {
    if (file.size > MAX_ATTACHMENT_BYTES) throw new Error(`${file.name} must be 6 MB or smaller.`);
    dataUrl = await fileToDataUrl(file);
  } else {
    dataUrl = await resizeImage(file);
    mimeType = 'image/jpeg';
  }
  if (dataUrlBytes(dataUrl) > MAX_ATTACHMENT_BYTES)
    throw new Error(`${file.name} is still too large after optimization.`);
  return {
    id: crypto.randomUUID(),
    name: file.name,
    mimeType,
    dataUrl,
    kind: mimeType === 'application/pdf' ? 'pdf' : 'image',
  };
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Unable to read ${file.name}.`));
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.readAsDataURL(file);
  });
}

async function resizeImage(file: File): Promise<string> {
  const image =
    typeof createImageBitmap === 'function' ? await createImageBitmap(file) : await loadImage(file);
  try {
    const longEdge = Math.max(image.width, image.height);
    const scale = Math.min(1, 2000 / longEdge);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Image optimization is unavailable.');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.82);
  } finally {
    if ('close' in image && typeof image.close === 'function') image.close();
  }
}

async function loadImage(file: File): Promise<HTMLImageElement> {
  const url = await fileToDataUrl(file);
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Unable to decode ${file.name}.`));
    image.src = url;
  });
}

function dataUrlBytes(value: string): number {
  const base64 = value.slice(value.indexOf(',') + 1);
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
}

function useSpeechDictation(onResult: (text: string) => void) {
  const constructorRef = useRef<(new () => SpeechRecognitionLike) | null>();
  if (constructorRef.current === undefined) {
    const browser = window as unknown as {
      SpeechRecognition?: new () => SpeechRecognitionLike;
      webkitSpeechRecognition?: new () => SpeechRecognitionLike;
    };
    constructorRef.current = browser.SpeechRecognition ?? browser.webkitSpeechRecognition ?? null;
  }
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const finalRef = useRef('');
  const resultRef = useRef(onResult);
  resultRef.current = onResult;

  function stop() {
    try {
      recognitionRef.current?.stop();
    } catch {
      /* already stopped */
    }
    setListening(false);
  }

  function start() {
    const Recognition = constructorRef.current;
    if (!Recognition) return;
    try {
      recognitionRef.current?.abort();
    } catch {
      /* already stopped */
    }
    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    finalRef.current = '';
    recognition.onresult = (event) => {
      let interim = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result?.[0]?.transcript ?? '';
        if (result?.isFinal) finalRef.current = `${finalRef.current} ${transcript}`.trim();
        else interim = `${interim} ${transcript}`.trim();
      }
      resultRef.current(`${finalRef.current} ${interim}`.trim());
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  }

  useEffect(
    () => () => {
      try {
        recognitionRef.current?.abort();
      } catch {
        /* already stopped */
      }
    },
    [],
  );
  return { supported: Boolean(constructorRef.current), listening, start, stop };
}
